// SPDX-License-Identifier: Apache-2.0
/**
 * Realtime events of a room's metadata (PROTOCOL.md §6) and what they do to the query cache. The payloads carry the
 * changed objects, so the cached room detail is patched in place (lib/roomCache.ts) instead of refetched: an admin
 * action costs its own request and nothing more. The room is refetched (coalesced and debounced per room) only when
 * the viewer's own room permissions can change: changes of a role the viewer holds (or @everyone), the viewer's own
 * membership, the ownership. The member list (`["members", roomId]`) and the room's member count follow
 * `member.join` / `member.leave` / `member.update` from their payloads as well. Channels
 * and categories the viewer gains or loses through any change arrive as their own `channel.*` / `category.*` events
 * (the node's per-connection visibility). The room list follows `room.update`, `room.delete` and the viewer's own
 * joins and leaves; the command palettes follow the events that change permissions.
 */
import type { QueryClient } from "@tanstack/react-query";
import type { Category, Channel, Member, PermissionOverride, Role, Room, RoomDetail, TavrnSocket, User } from "@tavrn/sdk";
import { cachedMembers, patchMembers, patchRoom, roomRefetcher, withCategory, withChannel, withJoined, withMember, withMemberCount, withOverrides, withRole, withRoom, withoutCategory, withoutChannel, withoutMember, withoutRole } from "../lib/roomCache";

/** Debounce of the room refetch: a burst of changes (a role reorder, a bot's setup) costs one request. */
export const ROOM_REFETCH_DELAY_MS = 250;

interface Removed {
  room_id: string;
  id: string;
}

interface OverridesChanged {
  room_id: string;
  target_type?: string;
  target_id?: string;
  overrides?: PermissionOverride[];
}

interface MembershipEvent {
  room_id: string;
  user_id?: string;
  /** `member.join`: the joined user. */
  user?: User;
  /** `member.update`: the member with its roles and room profile. */
  member?: Member;
}

export function roomEventHandlers(socket: TavrnSocket, queryClient: QueryClient, me: () => { id: string } | null): Array<() => void> {
  const refetch = roomRefetcher(queryClient, ROOM_REFETCH_DELAY_MS);
  const rooms = () => void queryClient.invalidateQueries({ queryKey: ["rooms"] });
  // Application commands (APPLICATIONS.md §5.4): the palettes follow definitions and the permissions of the viewer.
  const commands = () => void queryClient.invalidateQueries({ queryKey: ["commands"] });
  const own = (d: MembershipEvent) => {
    const id = d.user_id ?? d.user?.id ?? d.member?.user?.id;
    // Without a user the event may concern the viewer.
    return !id || id === me()?.id;
  };
  /** Does the viewer's room permission depend on this role? Not for the owner (who holds every permission). */
  const holdsRole = (roomId: string, roleId: string, everyone: boolean) => {
    const room = queryClient.getQueryData<RoomDetail>(["room", roomId]);
    return !!room && !room.is_owner && (everyone || room.my_roles.includes(roleId));
  };
  return [
    socket.on<Channel>("channel.create", (c) => patchRoom(queryClient, c.room_id, withChannel(c))),
    socket.on<Channel>("channel.update", (c) => patchRoom(queryClient, c.room_id, withChannel(c))),
    socket.on<Removed>("channel.delete", (d) => patchRoom(queryClient, d.room_id, withoutChannel(d.id))),
    socket.on<Category>("category.create", (c) => patchRoom(queryClient, c.room_id, withCategory(c))),
    socket.on<Category>("category.update", (c) => patchRoom(queryClient, c.room_id, withCategory(c))),
    socket.on<Removed>("category.delete", (d) => patchRoom(queryClient, d.room_id, withoutCategory(d.id))),
    socket.on<OverridesChanged>("override.update", (d) => {
      if (d.target_type && d.target_id && Array.isArray(d.overrides)) patchRoom(queryClient, d.room_id, withOverrides(d.target_type, d.target_id, d.overrides));
      else refetch.schedule(d.room_id);
      commands();
    }),
    // A role the viewer holds (or @everyone) can change the viewer's `my_permissions`, which the node computes: those
    // refetch the room (checked before the patch). A new role is nobody's yet.
    socket.on<Role>("role.create", (r) => {
      patchRoom(queryClient, r.room_id, withRole(r));
      commands();
    }),
    socket.on<Role>("role.update", (r) => {
      const mine = holdsRole(r.room_id, r.id, r.is_everyone);
      patchRoom(queryClient, r.room_id, withRole(r));
      if (mine) refetch.schedule(r.room_id);
      commands();
    }),
    socket.on<Removed>("role.delete", (d) => {
      const mine = holdsRole(d.room_id, d.id, false);
      patchRoom(queryClient, d.room_id, withoutRole(d.id));
      if (mine) refetch.schedule(d.room_id);
      commands();
    }),
    socket.on<Room>("room.update", (r) => {
      // `room.update` carries the room without the viewer's fields: a new owner changes them.
      const before = queryClient.getQueryData<{ owner_id?: string; privacy_notice_updated_at?: string | null; legal_notice_updated_at?: string | null }>(["room", r.id]);
      const owner = before?.owner_id;
      patchRoom(queryClient, r.id, withRoom(r));
      // The room's documents changed (PROTOCOL.md §6b): open document views load them again.
      const changed = (a?: string | null, b?: string | null) => (a ?? null) !== (b ?? null);
      if (before && (changed(before.privacy_notice_updated_at, r.privacy_notice_updated_at) || changed(before.legal_notice_updated_at, r.legal_notice_updated_at))) {
        void queryClient.invalidateQueries({ predicate: (q) => q.queryKey[0] === "room-preview" && q.queryKey[2] === r.id });
      }
      if (owner !== undefined && r.owner_id !== undefined && owner !== r.owner_id) refetch.schedule(r.id);
      rooms();
    }),
    socket.on<{ id: string }>("room.delete", rooms),
    socket.on<MembershipEvent>("member.update", (d) => {
      if (d.member?.user) patchMembers(queryClient, d.room_id, withMember(d.member));
      if (own(d)) refetch.schedule(d.room_id);
      commands();
    }),
    // Another member's join or leave patches the member list and the member count. The viewer's own joins and leaves
    // (other devices, kicks, bans) change the room list and the viewer's permissions: those refetch.
    socket.on<MembershipEvent>("member.join", (d) => {
      if (!d.room_id) return;
      if (own(d)) {
        if (d.user) patchMembers(queryClient, d.room_id, withJoined(d.user));
        rooms();
        refetch.schedule(d.room_id);
        return;
      }
      if (!d.user) return refetch.schedule(d.room_id);
      // A member already listed is no new member (a repeated event): the count stays.
      const listed = !!cachedMembers(queryClient, d.room_id)?.some((m) => m.user.id === d.user!.id);
      patchMembers(queryClient, d.room_id, withJoined(d.user));
      if (!listed) patchRoom(queryClient, d.room_id, withMemberCount(1));
    }),
    socket.on<MembershipEvent>("member.leave", (d) => {
      if (!d.room_id) return;
      if (own(d)) {
        rooms();
        refetch.schedule(d.room_id);
        return;
      }
      const list = cachedMembers(queryClient, d.room_id);
      const listed = !list || list.some((m) => m.user.id === d.user_id);
      patchMembers(queryClient, d.room_id, withoutMember(d.user_id!));
      if (listed) patchRoom(queryClient, d.room_id, withMemberCount(-1));
    }),
    socket.on("commands.update", commands),
    refetch.stop,
  ];
}
